// The WALLS domain, part 1: the Wall DRAW tool (polyline draw with its
// vertex/wall-line/angle/alignment snapping) and the CORNER domain of the
// Select chain (lock markers + hover ring, press → click-toggles-lock /
// drag-moves-vertex, release, cancel). Same shape as the measure/openings
// slices: this file owns state + bodies; floor-mode keeps the Select chain's
// order and calls the hooks at the original priority spots.
//
// Part 2: the wall length-PILL side-flip drag and the wall-BODY translate drag
// (the Select chain's wall branches). The central selectWall orchestration and
// the wall dim-edit box stay in floor-mode (cross-domain).

import * as THREE from "three";
import { floorScene, floorTools, wallLabels, wallSystem } from "../core";
import { closestOnSeg } from "../geom";
import { BORDER_SCALE, VERTEX_SELECTED } from "../vertex-style";
import { M_TO_IN, formatLengthIn, getDisplayUnit } from "../../model/units";
import { uiState } from "../../ui/state";
import { desktopMouse } from "../../input/desktop";
import { type FloorTool as FloorToolId } from "../../ui/floor-ui";

const FLOOR_VERT_EPS = 0.15; // snap to an existing vertex within 15 cm
const FLOOR_ALIGN = 0.15; // align the wall end with another vertex's X/Z level within 15 cm

export interface WallsDomainDeps {
  activeCamera: () => THREE.Camera;
  canvasRect: () => DOMRect; // renderer canvas bounds, for screen-space picks
  activeTool: () => FloorToolId;
  isShiftHeld: () => boolean; // Shift frees the draw + corner snaps
  wallsOn: () => boolean; // corners are an affordance of VISIBLE walls
  showDrawLabel: (text: string, clientX: number, clientY: number) => void;
  hideDrawLabel: () => void;
  commitFloor: () => void;
  refreshSelectionUI: () => void; // wall fields follow a corner drag live
  snapToGrid: (v: number) => number; // the shared 5 cm position snap
  angleSnapRad: number; // draw direction snap (shared 15° step)
  axisSnapRad: number; // corner drag: within this of an axis → square it
  wallMoveSnapTan: number; // body drag: axis-lock window (tan of the angle)
  selectWall: (id: number | null) => void; // central selection (clears others)
  occluderDist: () => number; // the side-elevation pick barrier (floor-mode)
}

export interface WallsDomainApi {
  // Wall draw tool (registered as the "wall" floor tool internally).
  finishDraw: () => void; // end the polyline (Esc / tool switches / new layout)
  // Corner domain (Select chain).
  updateCornerHover: () => number | null; // markers + hover ring; the corner under the cursor
  pressCorner: (hoverVid: number | null) => boolean; // arm click-lock / drag-move
  dragCorner: (fp: { x: number; z: number } | null) => boolean; // claimed this frame?
  cornerDragArmed: () => boolean; // the shared wasDragging term
  disarmCorner: () => void; // another branch claimed the press → no corner drag
  releaseCorner: () => void; // click (no drag) → toggle the corner's lock
  cancelCornerGesture: () => void;
  // Legacy selection stub: corners no longer have a persistent "selected"
  // state — a click just toggles the lock. Mode/view/walls-off transitions
  // (and Escape) still call this; it just clears the transient hover ring.
  selectFloorVertex: (vid: number | null) => void;
  updateMarkerScale: () => void; // corner markers scale WITH the drawing
  // Wall length-pill + wall-body gestures (Select chain).
  grabDimPill: () => boolean; // press on a floating length pill (2D plan)
  pillDragArmed: () => boolean; // gates the rest of the press chain
  armWallDrag: (wid: number, fp: { x: number; z: number }) => void;
  dragWallPill: (fp: { x: number; z: number } | null) => boolean;
  dragWallBody: (fp: { x: number; z: number } | null) => boolean;
  bodyDragArmed: () => boolean; // the shared wasDragging term
  clearWallGestures: () => void; // press preamble / bulk release / cancel
}

export function installWallsDomain(deps: WallsDomainDeps): WallsDomainApi {
  // ── Wall draw tool ──────────────────────────────────────────────────────
  let drawActive = false; // mid-polyline
  let drawStart: { x: number; z: number } | null = null;
  let drawFirst: { x: number; z: number } | null = null; // polyline start (loop close)

  // Nearest point ON an existing wall line within FLOOR_VERT_EPS (T-junction
  // snap), or null. Shift bypasses.
  function snapToWallLine(p: { x: number; z: number }): { x: number; z: number } | null {
    if (deps.isShiftHeld()) return null;
    let best: { x: number; z: number } | null = null;
    let bestD = FLOOR_VERT_EPS;
    wallSystem.forEachSegment((ax, az, bx, bz) => {
      const c = closestOnSeg(p.x, p.z, ax, az, bx, bz);
      if (c.d < bestD) {
        bestD = c.d;
        best = { x: c.x, z: c.z };
      }
    });
    return best;
  }
  // First/free point: snap to an existing vertex, else onto a wall line, else
  // to the grid.
  function snapPoint(p: { x: number; z: number }): { x: number; z: number } {
    let best: { x: number; z: number } | null = null;
    let bestD = FLOOR_VERT_EPS;
    for (const v of wallSystem.vertexPositions()) {
      const d = Math.hypot(v.x - p.x, v.z - p.z);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    if (best) return best;
    return snapToWallLine(p) ?? { x: deps.snapToGrid(p.x), z: deps.snapToGrid(p.z) };
  }
  // Slide the end (along the already-angle-snapped direction from `from`) so it
  // lands on the horizontal/vertical guide line through an existing wall vertex,
  // when one is within FLOOR_ALIGN. Keeps the wall's direction exactly — only
  // its length changes — so e.g. a perpendicular wall's end snaps level with
  // another wall's end (an easy right-angle hook-up). Returns the closest such
  // end, or null when none is near. Only the axis the direction can actually
  // reach is considered (a vertical wall can't change its X by extending), and
  // only forward extensions count.
  function alignWallEnd(
    from: { x: number; z: number },
    dirx: number,
    dirz: number,
    ex: number,
    ez: number,
  ): { x: number; z: number } | null {
    let best: { x: number; z: number } | null = null;
    let bestMove = FLOOR_ALIGN;
    for (const v of wallSystem.vertexPositions()) {
      // X-guide: extend until end.x == v.x (needs horizontal travel).
      if (Math.abs(dirx) > 1e-3 && Math.abs(ex - v.x) <= FLOOR_ALIGN) {
        const t = (v.x - from.x) / dirx;
        if (t > 0) {
          const c = { x: v.x, z: from.z + dirz * t };
          const move = Math.hypot(c.x - ex, c.z - ez);
          if (move < bestMove) {
            bestMove = move;
            best = c;
          }
        }
      }
      // Z-guide: extend until end.z == v.z (needs vertical travel).
      if (Math.abs(dirz) > 1e-3 && Math.abs(ez - v.z) <= FLOOR_ALIGN) {
        const t = (v.z - from.z) / dirz;
        if (t > 0) {
          const c = { x: from.x + dirx * t, z: v.z };
          const move = Math.hypot(c.x - ex, c.z - ez);
          if (move < bestMove) {
            bestMove = move;
            best = c;
          }
        }
      }
    }
    return best;
  }
  // Segment endpoint: vertex snap wins; then a point on an existing wall line
  // (T-junction); else snap angle (to 15°), then align the end with another
  // vertex's X/Z level if close, else snap length (5cm) relative to `from`.
  // Shift frees the angle/length, wall-line, and alignment snaps.
  function snapSegmentEnd(
    from: { x: number; z: number },
    p: { x: number; z: number },
  ): { x: number; z: number } {
    for (const v of wallSystem.vertexPositions()) {
      if (Math.hypot(v.x - p.x, v.z - p.z) < FLOOR_VERT_EPS) return v;
    }
    const wl = snapToWallLine(p);
    if (wl) return wl;
    let ang = Math.atan2(p.z - from.z, p.x - from.x);
    let len = Math.hypot(p.x - from.x, p.z - from.z);
    if (!deps.isShiftHeld()) {
      ang = Math.round(ang / deps.angleSnapRad) * deps.angleSnapRad;
      const dirx = Math.cos(ang);
      const dirz = Math.sin(ang);
      // End at the raw cursor distance along the snapped direction, then try to
      // land it on a vertex's alignment guide (right-angle hook-up helper).
      const aligned = alignWallEnd(from, dirx, dirz, from.x + dirx * len, from.z + dirz * len);
      if (aligned) return aligned;
      len = deps.snapToGrid(len);
    }
    return { x: from.x + Math.cos(ang) * len, z: from.z + Math.sin(ang) * len };
  }

  function finishDraw(): void {
    drawActive = false;
    drawStart = null;
    drawFirst = null;
    wallSystem.hidePreview();
    deps.hideDrawLabel();
  }

  floorTools.register({
    id: "wall",
    frame: ({ fp, press }) => {
      if (!drawActive) {
        wallSystem.hidePreview();
        deps.hideDrawLabel();
        if (press && fp) {
          drawStart = snapPoint(fp);
          drawFirst = drawStart;
          drawActive = true;
        }
      } else if (fp && drawStart) {
        const end = snapSegmentEnd(drawStart, fp);
        wallSystem.setPreview(drawStart.x, drawStart.z, end.x, end.z);
        const len = Math.hypot(end.x - drawStart.x, end.z - drawStart.z);
        const deg = Math.round((Math.atan2(end.z - drawStart.z, end.x - drawStart.x) * 180) / Math.PI);
        deps.showDrawLabel(
          `${formatLengthIn(len * M_TO_IN, getDisplayUnit())}  ${deg}°`,
          desktopMouse.clientX,
          desktopMouse.clientY,
        );
        if (press && len > 1e-3) {
          wallSystem.addWall(drawStart.x, drawStart.z, end.x, end.z);
          deps.commitFloor(); // each drawn segment is its own undo step
          // Closing back on the polyline start finishes the loop.
          if (drawFirst && Math.hypot(end.x - drawFirst.x, end.z - drawFirst.z) < 1e-4) {
            finishDraw();
          } else {
            drawStart = end;
          }
        }
      }
    },
  });

  // ── Corner domain (Select chain) ────────────────────────────────────────
  // Hovering a corner reveals every corner (yellow = free, red = locked) and
  // outlines the one under the cursor in WHITE. Clicking a corner toggles its
  // lock (yellow ↔ red) directly — there's no separate "selected" state,
  // mirroring how a door/window jamb locks.
  let dragVid: number | null = null; // dragging a wall corner
  let pressVid: number | null = null;
  let pressVidMoved = false;
  const _pressStart = new THREE.Vector2();

  // Corner hover ring: a WHITE border behind the hovered corner's dot (the dot
  // is drawn by walls.ts at renderOrder 20, so this sits at 19 and only its rim
  // shows) — matching the measurement-vertex hover border. transparent + no
  // depth write so it draws over the see-through grid.
  const SEL_MARKER_R = 0.1 / 1.5; // = the normal corner marker (CORNER_R)
  const hoverMarker = new THREE.Mesh(
    new THREE.SphereGeometry(SEL_MARKER_R * BORDER_SCALE, 28, 20),
    new THREE.MeshBasicMaterial({
      color: VERTEX_SELECTED,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    }),
  );
  hoverMarker.renderOrder = 19; // under the corner dots (20) → only the rim shows
  hoverMarker.visible = false;
  floorScene.root.add(hoverMarker);
  // World-fixed scale for the corner markers — they scale WITH the drawing
  // (grow when you zoom in, shrink when you zoom out), staying the same size
  // relative to the walls instead of holding a fixed pixel size.
  const MARKER_SCREEN_SIZE = 1.5;
  function updateMarkerScale(): void {
    wallSystem.setMarkerScale(MARKER_SCREEN_SIZE);
    hoverMarker.scale.setScalar(MARKER_SCREEN_SIZE);
  }
  // Place the white hover ring on a corner (null hides it).
  function setHoverMarker(vid: number | null): void {
    const v = vid !== null ? wallSystem.vertexPos(vid) : null;
    if (!v) {
      hoverMarker.visible = false;
      return;
    }
    hoverMarker.position.set(v.x, wallSystem.height(), v.z);
    hoverMarker.visible = true;
  }

  // The wall corner whose screen position is within `maxPx` of (px,py), nearest
  // first, or null. Screen-space so it's accurate in both the 2D plan and the
  // 3D view, and respects zoom.
  const _vScreen = new THREE.Vector3();
  function pickVertexScreen(px: number, py: number, maxPx = 15): number | null {
    const cam = deps.activeCamera();
    cam.updateMatrixWorld();
    const r = deps.canvasRect();
    let best: number | null = null;
    let bestD = maxPx;
    for (const v of wallSystem.vertexEntries()) {
      _vScreen.set(v.x, wallSystem.height(), v.z).project(cam);
      if (_vScreen.z > 1) continue; // behind the camera
      const sx = (_vScreen.x * 0.5 + 0.5) * r.width + r.left;
      const sy = (-_vScreen.y * 0.5 + 0.5) * r.height + r.top;
      const d = Math.hypot(sx - px, sy - py);
      if (d < bestD) {
        bestD = d;
        best = v.id;
      }
    }
    return best;
  }

  // Per-frame corner affordance: corners are a 2D-plan feature of VISIBLE
  // walls. In Select, reveal all corners only while actively hovering one;
  // otherwise hide them. The Wall tool shows only the pinned ones so you can
  // still see what's locked while editing walls; other tools hide them all.
  // Returns the hovered corner (Select tool only) for the press chain.
  function updateCornerHover(): number | null {
    const tool = deps.activeTool();
    const cornersOn = deps.wallsOn() && uiState.floorView === "2d";
    const hoverVid =
      tool === "select" && cornersOn && !desktopMouse.camDragging
        ? pickVertexScreen(desktopMouse.clientX, desktopMouse.clientY)
        : null;
    if (!cornersOn) {
      wallSystem.hideLockMarkers(); // 3D or walls-off → no corner markers at all
    } else if (tool === "select") {
      if (hoverVid !== null) {
        wallSystem.refreshLockMarkers(true);
      } else {
        wallSystem.hideLockMarkers();
      }
    } else if (tool === "wall") {
      wallSystem.refreshLockMarkers(false);
    } else {
      wallSystem.hideLockMarkers();
    }
    setHoverMarker(tool === "select" ? hoverVid : null); // gray ring on the hovered corner
    return hoverVid;
  }

  // Press on a corner: a plain release (no drag) toggles its lock; an unlocked
  // corner also drags to move (a locked one doesn't — click it to unlock first).
  function pressCorner(hoverVid: number | null): boolean {
    if (hoverVid === null) return false;
    pressVid = hoverVid;
    _pressStart.set(desktopMouse.clientX, desktopMouse.clientY);
    dragVid = wallSystem.isVertexLocked(hoverVid) ? null : hoverVid;
    return true;
  }

  // Drag a corner. Don't move until the press clears a small dead-zone —
  // otherwise a plain click on a corner (to lock it) would nudge the vertex by
  // the snap. Claims the frame only under the original armed + held gate.
  function dragCorner(fp: { x: number; z: number } | null): boolean {
    if (dragVid === null || !desktopMouse.lmb || !fp) return false;
    if (
      !pressVidMoved &&
      Math.hypot(
        desktopMouse.clientX - _pressStart.x,
        desktopMouse.clientY - _pressStart.y,
      ) > 4
    ) {
      pressVidMoved = true;
    }
    if (pressVidMoved) {
      let s = snapPoint(fp);
      // Square up the corner: snap incident walls to the world axes (right
      // angles) when close. Shift frees it for an arbitrary angle.
      if (!deps.isShiftHeld()) s = wallSystem.snapVertexAxis(dragVid, s.x, s.z, deps.axisSnapRad);
      wallSystem.moveVertex(dragVid, s.x, s.z);
      deps.refreshSelectionUI();
    }
    return true;
  }

  // Toggle a corner's lock (yellow ↔ red) — a plain click, like a wall jamb.
  function releaseCorner(): void {
    if (pressVid !== null && !pressVidMoved) {
      wallSystem.toggleVertexLock(pressVid);
      deps.commitFloor(); // one undo step per toggle, like the Lock tool
    }
    pressVid = null;
    pressVidMoved = false;
  }

  // ── Wall length-pill side-flip + wall-body translate (Select chain) ──────
  const _ray = new THREE.Raycaster();
  let dimDragWid: number | null = null; // dragging a wall's dimension pill to a side
  const _dimDragStart = new THREE.Vector2(); // press NDC, to tell a click from a drag
  let dimDragMoved = false; // the pill has actually been dragged (not just clicked)
  // Dragging a wall's BODY translates it; per-frame deltas are applied against
  // the grid-snapped TOTAL so shared corners follow without drift.
  let wallDrag: {
    wid: number;
    grabX: number;
    grabZ: number;
    appliedX: number;
    appliedZ: number;
  } | null = null;

  // Try to grab a wall's floating dimension pill (2D plan only) for a side-flip
  // drag. Called LAST in the pick order so on-wall controls (door handles,
  // corners, the wall body) win where the pill's billboard overlaps them.
  function grabDimPill(): boolean {
    if (uiState.floorView !== "2d" || !wallLabels.group.visible) return false;
    _ray.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    // Raycaster doesn't skip invisible objects, and pooled/hidden pills carry a
    // null id, so take the first VISIBLE hit with a real wall id.
    const hits = _ray.intersectObjects(wallLabels.group.children, false);
    const lbl = hits.find(
      (h) => h.object.visible && typeof h.object.userData.wallId === "number",
    );
    const wid = lbl?.object.userData.wallId as number | undefined;
    if (typeof wid !== "number") return false;
    if (lbl!.distance > deps.occluderDist()) return false; // through a wall (side elevation)
    dimDragWid = wid;
    _dimDragStart.copy(desktopMouse.ndc);
    dimDragMoved = false;
    deps.selectWall(wid);
    dragVid = null; // the pill grab supersedes any corner drag
    return true;
  }

  // Drag the pill to whichever side of the wall the cursor is on. The pill
  // renders on the OPPOSITE face from the one being measured (so it sits
  // outside the room), so the measured side is the inverse of the cursor's
  // side — which keeps the pill tracking the cursor instead of running away.
  // A plain click just selects (the pill-click handler also opens its length
  // editor) — only a real drag past a small dead-zone moves/pins the dim side.
  function dragWallPill(fp: { x: number; z: number } | null): boolean {
    if (dimDragWid === null || !desktopMouse.lmb || !fp) return false;
    if (!dimDragMoved && _dimDragStart.distanceTo(desktopMouse.ndc) > 0.012) {
      dimDragMoved = true;
    }
    const wi = dimDragMoved ? wallSystem.info(dimDragWid) : null;
    if (wi) {
      const ex = wi.bx - wi.ax;
      const ez = wi.bz - wi.az;
      const L = Math.hypot(ex, ez) || 1;
      const nx = -ez / L; // left normal
      const nz = ex / L;
      const d = (fp.x - (wi.ax + wi.bx) / 2) * nx + (fp.z - (wi.az + wi.bz) / 2) * nz;
      wallSystem.setDimSide(dimDragWid, d >= 0 ? -1 : 1);
      deps.refreshSelectionUI();
    }
    return true;
  }

  // Translate the wall. Project the total cursor delta onto the wall's own
  // axes — along its length (slide) and perpendicular (push in/out). Only LOCK
  // to an axis when the drag is within the snap window of it; wider drags move
  // freely (still grid-snapped per axis). Shift = free 2D, no snap. Applying
  // only the per-frame change keeps shared corners following.
  function dragWallBody(fp: { x: number; z: number } | null): boolean {
    if (wallDrag === null || !desktopMouse.lmb || !fp) return false;
    const wx = fp.x - wallDrag.grabX;
    const wz = fp.z - wallDrag.grabZ;
    let tx = wx;
    let tz = wz;
    const wi = deps.isShiftHeld() ? null : wallSystem.info(wallDrag.wid);
    if (wi) {
      const ex = wi.bx - wi.ax;
      const ez = wi.bz - wi.az;
      const L = Math.hypot(ex, ez) || 1;
      const ux = ex / L; // along the wall
      const uz = ez / L;
      const nx = -uz; // perpendicular to the wall
      const nz = ux;
      const along = wx * ux + wz * uz;
      const perp = wx * nx + wz * nz;
      const aAbs = Math.abs(along);
      const pAbs = Math.abs(perp);
      let a = deps.snapToGrid(along);
      let p = deps.snapToGrid(perp);
      // Near-parallel drag → drop the tiny perpendicular wobble, and vice
      // versa. In between, keep both (free diagonal move within the frame).
      if (pAbs <= aAbs * deps.wallMoveSnapTan) p = 0;
      else if (aAbs <= pAbs * deps.wallMoveSnapTan) a = 0;
      tx = ux * a + nx * p;
      tz = uz * a + nz * p;
    }
    const dx = tx - wallDrag.appliedX;
    const dz = tz - wallDrag.appliedZ;
    if (dx !== 0 || dz !== 0) {
      wallSystem.translateWall(wallDrag.wid, dx, dz);
      wallDrag.appliedX = tx;
      wallDrag.appliedZ = tz;
      deps.refreshSelectionUI();
    }
    return true;
  }

  return {
    finishDraw,
    updateCornerHover,
    pressCorner,
    dragCorner,
    cornerDragArmed: () => dragVid !== null,
    disarmCorner: () => {
      dragVid = null;
    },
    releaseCorner,
    cancelCornerGesture: () => {
      dragVid = null;
      pressVid = null;
      pressVidMoved = false;
    },
    selectFloorVertex: (_vid) => {
      hoverMarker.visible = false;
    },
    updateMarkerScale,
    grabDimPill,
    pillDragArmed: () => dimDragWid !== null,
    armWallDrag: (wid, fp) => {
      wallDrag = { wid, grabX: fp.x, grabZ: fp.z, appliedX: 0, appliedZ: 0 };
    },
    dragWallPill,
    dragWallBody,
    bodyDragArmed: () => wallDrag !== null,
    clearWallGestures: () => {
      dimDragWid = null;
      dimDragMoved = false;
      wallDrag = null;
    },
  };
}
